import React, { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import SeverityBadge from "../components/SeverityBadge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime } from "../lib/format";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function Alerts() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [severityFilter, setSeverityFilter] = useState("ALL");
  const { user } = useAuth();
  const canResolve = user && (user.role === "admin" || user.role === "risk_officer");

  const load = () => {
    setLoading(true);
    api.get("/alerts").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  const filtered = useMemo(() => {
    return data.filter((a) => {
      if (filter !== "ALL" && a.alert_type !== filter) return false;
      if (severityFilter !== "ALL" && a.severity !== severityFilter) return false;
      return true;
    });
  }, [data, filter, severityFilter]);

  const handleRegenerate = async () => {
    try {
      const res = await api.post("/alerts/regenerate");
      toast.success(`${res.data.generated} alert dibuat ulang`);
      load();
    } catch {
      toast.error("Gagal regenerate alert");
    }
  };

  const handleResolve = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`);
      toast.success("Alert ditutup");
      load();
    } catch {
      toast.error("Gagal menutup alert");
    }
  };

  const types = useMemo(() => Array.from(new Set(data.map((a) => a.alert_type))).sort(), [data]);

  return (
    <div className="p-8 max-w-[1700px] mx-auto" data-testid="alerts-page">
      <PageHeader
        title="Early Warning System"
        subtitle={`${data.length} alert tercatat`}
        action={canResolve && (
          <Button onClick={handleRegenerate} variant="outline" data-testid="regenerate-alerts-btn">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Regenerate
          </Button>
        )}
      />

      <div className="flex items-center gap-3 mb-4">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56"><SelectValue placeholder="Tipe alert" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua tipe</SelectItem>
            {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={severityFilter} onValueChange={setSeverityFilter}>
          <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Semua severity</SelectItem>
            <SelectItem value="critical">Kritis</SelectItem>
            <SelectItem value="warning">Peringatan</SelectItem>
            <SelectItem value="info">Informasi</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <DataTable
          testId="alerts-table"
          searchKeys={["alert_id", "alert_type", "message", "related_entity_id"]}
          columns={[
            { header: "Tipe", cell: (r) => <span className="mono text-xs font-medium">{r.alert_type}</span> },
            { header: "Severity", cell: (r) => <SeverityBadge severity={r.severity} /> },
            { header: "Entity", cell: (r) => (
              <div>
                <div className="text-xs">{r.related_entity_type}</div>
                <div className="mono text-[10px] text-muted-foreground">{r.related_entity_id}</div>
              </div>
            ) },
            { header: "Pesan", cell: (r) => <span className="text-xs">{r.message}</span> },
            { header: "Dibuat", cell: (r) => <span className="mono text-xs">{formatDateTime(r.created_at)}</span> },
            { header: "Status", cell: (r) => r.status === "Resolved" ? <span className="text-xs text-emerald-700 font-medium">Resolved</span> : <span className="text-xs font-medium">Open</span> },
            ...(canResolve ? [{
              header: "Aksi", cell: (r) => r.status === "Open" ? (
                <Button size="sm" variant="ghost" onClick={() => handleResolve(r.alert_id)} data-testid={`resolve-${r.alert_id}`}>Tutup</Button>
              ) : null
            }] : []),
          ]}
          data={filtered}
          pageSize={20}
        />
      )}
    </div>
  );
}
